import { useState, useEffect, useRef } from 'react';
import { Column, ColumnEditorOptions } from 'primereact/column';
import { Card } from 'primereact/card';
import { ProductHierarchyService } from '../../service/ProductHierarchyService';
import React from 'react';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import ProductHierarchy from '../../models/ProductHierarchy';
import { DataTable, DataTableExpandedRows, DataTableSelectionSingleChangeEvent, DataTableValueArray } from 'primereact/datatable';
import { Controller, useForm } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';

const ProductHierarchies = () => {
    let emptyProductHierarchy : ProductHierarchy = {
        id: 0,
        key: '',
        hasChildren: false,
        levelName: '',
        level1: '',
        level2: '',
        level3: '',
        description: '',
        displayVds: false,
        onlineOnly: false,
        fanwear: false
    }

    const [productHierarchy, setProductHierarchy] = useState<ProductHierarchy[]>([]);
    const [originalProductHierarchy, setOriginalProductHierarchy] = useState<ProductHierarchy[]>([]);
    const [selectedProductHierarchy, setSelectedProductHierarchy] = useState<ProductHierarchy>(emptyProductHierarchy);
    const [loadingProductHierarchy, setLoadingProductHierarchy] = useState(true);
    const [editingRows, setEditingRows] = useState({});
    const [expandedRowsLevel1, setExpandedRowsLevel1] = useState<DataTableExpandedRows | DataTableValueArray | undefined>(undefined);
    const [expandedRowsLevel2, setExpandedRowsLevel2] = useState<DataTableExpandedRows | DataTableValueArray | undefined>(undefined);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const toast = useRef<any>(null);
    const chkDisplayVds = useRef<any>(null);
    const chkOnlineOnly = useRef<any>(null);
    const chkFanwear = useRef<any>(null);

    useEffect(() => {
        ProductHierarchyService.getProductHierarchy().then(data => {
            setProductHierarchy(data);
            setOriginalProductHierarchy(data);
            setLoadingProductHierarchy(false);
        });
    }, []);

    const formEditProductHierarchy = useForm({ mode: "all"});
    
    const saveProductHierarchy = (productHierarchyData: ProductHierarchy) => {
        let _productHierarchy = [...productHierarchy];

        ProductHierarchyService.updateProductHierarchy(productHierarchyData).then((data) => {
            if (data) {
                let productHierarchyIndex = productHierarchy.findIndex(b => b.id === productHierarchyData.id);
                _productHierarchy[productHierarchyIndex] = productHierarchyData;
                setProductHierarchy(_productHierarchy);
                toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Product Hierarchy Updated', life: 3000 });
                setSelectedProductHierarchy(emptyProductHierarchy);
            } else {
                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Update Product Hierarchy', life: 3000 });
            }
        });
    }

    const onProductHierarchySelectionChange = (rowData: DataTableSelectionSingleChangeEvent<ProductHierarchy[]>) => {
        let selectedProductHierarchyIndex = productHierarchy.findIndex(v => v.id === (rowData.value as ProductHierarchy).id);
        let _editingRows = { ...editingRows, ...{ [`${productHierarchy[selectedProductHierarchyIndex].id}`]: true } };
        if(Object.keys(_editingRows).length === 1) {
            setEditingRows(_editingRows);
        }
    };

    const onRowEditChange = (e: any) => {
        setEditingRows(e.data);
    }

    const descriptionTemplate = (rowData: ProductHierarchy) => {
        return (
            <span>{rowData.description} (<span className="font-bold text-primary">{rowData.levelName}</span>)</span>
        );
    }

    const displayVdsTemplate = (rowData: ProductHierarchy) => {
        if (rowData.displayVds !== null) {
            if (rowData.displayVds) {
                return (
                    <React.Fragment>
                        <i className="pi pi-check text-green-500"></i>
                    </React.Fragment>
                );
            } else {
                return (
                    <React.Fragment>
                        <i className="pi pi-times text-red-500"></i>
                    </React.Fragment>
                );
            }
        }
    }

    const onlineOnlyTemplate = (rowData: ProductHierarchy) => {
        if (rowData.onlineOnly !== null) {
            if (rowData.onlineOnly) {
                return (
                    <React.Fragment>
                        <i className="pi pi-check text-green-500"></i>
                    </React.Fragment>
                );
            } else {
                return (
                    <React.Fragment>
                        <i className="pi pi-times text-red-500"></i>
                    </React.Fragment>
                );
            }
        }
    }

    const fanwearTemplate = (rowData: ProductHierarchy) => {
        if (rowData.fanwear !== null) {
            if (rowData.fanwear) {
                return (
                    <React.Fragment>
                        <i className="pi pi-check text-green-500"></i>
                    </React.Fragment>
                );
            } else {
                return (
                    <React.Fragment>
                        <i className="pi pi-times text-red-500"></i>
                    </React.Fragment>
                );
            }
        }
    }

    const displayVdsEditor = (options: any) => {
        return (
            <Controller
                name="displayVdsEdit"
                control={formEditProductHierarchy.control}
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    selectedProductHierarchy.displayVds = field.value;

                    return (
                        <>
                            <Checkbox ref={chkDisplayVds} inputId={field.name} checked={fieldState.isDirty ? field.value : options.value} inputRef={field.ref} className={classNames({ 'p-invalid mr-1': fieldState.error })} onChange={(e) => { selectedProductHierarchy.displayVds = e.target.value; field.onChange(e.checked); }} />
                        </>
                    )}
                }
            />
        );
    }

    const onlineOnlyEditor = (options: any) => {
        return (
            <Controller
                name="onlineOnlyEdit"
                control={formEditProductHierarchy.control}
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    selectedProductHierarchy.onlineOnly = field.value;

                    return (
                        <>
                            <Checkbox ref={chkOnlineOnly} inputId={field.name} checked={fieldState.isDirty ? field.value : options.value} inputRef={field.ref} className={classNames({ 'p-invalid mr-1': fieldState.error })} onChange={(e) => { selectedProductHierarchy.onlineOnly = e.target.value; field.onChange(e.checked) }} />
                        </>
                    )}
                }
            />
        );
    }

    const fanwearEditor = (options: any) => {
        return (
            <Controller
                name="fanWearEdit"
                control={formEditProductHierarchy.control}
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    selectedProductHierarchy.fanwear = field.value;

                    return (
                        <>
                            <Checkbox ref={chkFanwear} inputId={field.name} checked={fieldState.isDirty ? field.value : options.value} inputRef={field.ref} className={classNames({ 'p-invalid mr-1': fieldState.error })} onChange={(e) => { selectedProductHierarchy.fanwear = e.target.value; field.onChange(e.checked) }} />
                        </>
                    )}
                }
            />
        );
    }

    const actionTemplate = (rowData: ProductHierarchy, props: any) => {
        const rowEditor = props.rowEditor;
        
        if (rowEditor.editing) {
            return (
                <div className="flex flex-wrap gap-2">
                    <Button icon="p-row-editor-save-icon pi pi-fw pi-save" title="Save" rounded text severity="info" type="submit" onClick={(e) => {
                        let _selectedProductHierarchy = { ...selectedProductHierarchy };
                        _selectedProductHierarchy.id = rowData.id;
                        _selectedProductHierarchy.key = rowData.key;
                        _selectedProductHierarchy.hasChildren = rowData.hasChildren;
                        _selectedProductHierarchy.description = rowData.description;
                        _selectedProductHierarchy.level1 = rowData.level1;
                        _selectedProductHierarchy.level2 = rowData.level2;
                        _selectedProductHierarchy.level3 = rowData.level3;
                        _selectedProductHierarchy.levelName = rowData.levelName;
                        _selectedProductHierarchy.displayVds = chkDisplayVds.current.props.checked;
                        _selectedProductHierarchy.onlineOnly = chkOnlineOnly.current.props.checked;
                        _selectedProductHierarchy.fanwear = chkFanwear.current.props.checked;
                        saveProductHierarchy(_selectedProductHierarchy);
                        formEditProductHierarchy.reset();
                        selectedProductHierarchy.id = rowData.id;
                        rowEditor.onSaveClick(e);
                    }}></Button>

                    <Button type="button" icon="p-row-editor-cancel-icon pi pi-fw pi-undo" title="Undo" rounded text severity="danger" onClick={(e) => {
                        formEditProductHierarchy.clearErrors();
                        formEditProductHierarchy.reset();
                        rowEditor.onCancelClick(e);
                    }}></Button>
                </div>
            );
        }
    };

    const allowExpansion = (rowData: ProductHierarchy) => {
        return rowData.hasChildren;
    };

    const level1ExpansionTemplate = (data: ProductHierarchy) => {
        return (
            <div className="m-0">
                <DataTable value={productHierarchy.filter((v) => v.level1 === data.level1 && v.level2 !== '' && v.level3 === '')} 
                    dataKey="id" size="small" rowHover={true} tableStyle={{tableLayout: 'fixed'}} showHeaders={false}
                    selectionMode="single" onSelectionChange={onProductHierarchySelectionChange}
                    editingRows={editingRows} editMode="row" onRowEditChange={onRowEditChange}
                    rowExpansionTemplate={level2ExpansionTemplate}
                    expandedRows={expandedRowsLevel2} onRowToggle={(e) => setExpandedRowsLevel2(e.data)}>
                    <Column expander={allowExpansion} style={{ width: '4rem', paddingLeft: '2rem' }} />
                    <Column field="description" body={descriptionTemplate} style={{ width: '30rem' }} />
                    <Column field="displayVds" body={displayVdsTemplate} editor={(options: ColumnEditorOptions) => displayVdsEditor(options)} style={{ width: '20rem' }} bodyStyle={{paddingLeft: "2.2rem"}} />
                    <Column field="onlineOnly" body={onlineOnlyTemplate} editor={(options: ColumnEditorOptions) => onlineOnlyEditor(options)} style={{ width: '20rem' }} bodyStyle={{paddingLeft: "2.2rem"}} />
                    <Column field="fanwear" body={fanwearTemplate} editor={(options: ColumnEditorOptions) => fanwearEditor(options)} style={{ width: '20rem' }} bodyStyle={{paddingLeft: "2.2rem"}} />
                    <Column rowEditor body={actionTemplate} style={{ width: '8rem' }} />
                </DataTable>
            </div>
        );
    };

    const level2ExpansionTemplate = (data: ProductHierarchy) => {
        return (
            <div className="m-0">
                <DataTable value={productHierarchy.filter((v) => v.level1 === data.level1 && v.level2 === data.level2 && v.level3 !== '')} 
                    dataKey="id" size="small" rowHover={true} tableStyle={{tableLayout: 'fixed'}} showHeaders={false}
                    selectionMode="single" onSelectionChange={onProductHierarchySelectionChange}
                    editingRows={editingRows} editMode="row" onRowEditChange={onRowEditChange}>
                    <Column expander={false} style={{ width: '3rem', paddingLeft: '4.5rem' }} />
                    <Column field="description" body={descriptionTemplate} style={{ width: '30rem' }} />
                    <Column field="displayVds" body={displayVdsTemplate} editor={(options: ColumnEditorOptions) => displayVdsEditor(options)} style={{ width: '20rem' }} bodyStyle={{paddingLeft: "1.4rem"}} />
                    <Column field="onlineOnly" body={onlineOnlyTemplate} editor={(options: ColumnEditorOptions) => onlineOnlyEditor(options)} style={{ width: '20rem' }} bodyStyle={{paddingLeft: "1.7rem"}} />
                    <Column field="fanwear" body={fanwearTemplate} editor={(options: ColumnEditorOptions) => fanwearEditor(options)} style={{ width: '20rem' }} bodyStyle={{paddingLeft: "2.1rem"}} />
                    <Column rowEditor body={actionTemplate} style={{ width: '8rem' }} />
                </DataTable>
            </div>
        );
    };

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setGlobalFilter(value);
        let _productHierarchy = [...originalProductHierarchy];
        let _cloneProductHierarchy = _productHierarchy.map(x => ({...x}));
        let _productHierarchyLevel1 = _cloneProductHierarchy.filter((v) => v.level1.length > 0 && v.level2.length === 0 && v.level3.length === 0);
        let _productHierarchyLevel2 = _cloneProductHierarchy.filter((v) => v.level1.length > 0 && v.level2.length > 0 && v.level3.length === 0);
        let _productHierarchyLevel3 = _cloneProductHierarchy.filter((v) => v.level1.length > 0 && v.level2.length > 0 && v.level3.length > 0);
        let filteredProductHierarchy: ProductHierarchy[] = [];

        if (value.length > 0) {
            _productHierarchyLevel3.forEach((l3) => {
                if (l3.description.toLowerCase().includes(value.toLowerCase()) || l3.levelName.includes(value)) {
                    if (filteredProductHierarchy.filter(v => v.id === l3.id).length === 0) {
                        filteredProductHierarchy.push(l3);
                    }
                }
            });

            let l2Parents = filteredProductHierarchy.map(p => { return  p.level2; }).filter((v, i, a) => a.indexOf(v) === i);
            
            _productHierarchyLevel2.forEach((l2) => {
                if (l2.description.toLowerCase().includes(value.toLowerCase()) || l2.levelName.includes(value) || 
                    (l2Parents.includes(l2.level2) && l2.level3.length === 0)) {
                    if (filteredProductHierarchy.filter(v => v.id === l2.id).length === 0) {
                        filteredProductHierarchy.push(l2);
                    }
                }
            });

            let l1Parents = filteredProductHierarchy.map(p => { return  p.level1; }).filter((v, i, a) => a.indexOf(v) === i);
            
            _productHierarchyLevel1.forEach((l1) => {
                if (l1.description.toLowerCase().includes(value.toLowerCase()) || l1.levelName.includes(value) || 
                    (l1Parents.includes(l1.level1) && l1.level2.length === 0 && l1.level3.length === 0)) {
                    if (filteredProductHierarchy.filter(v => v.id === l1.id).length === 0) {
                        filteredProductHierarchy.push(l1);
                    }
                }
            });

            setProductHierarchy(filteredProductHierarchy);
        } else {
            let _originalProductHierarchy = [ ...originalProductHierarchy];
            setProductHierarchy(_originalProductHierarchy);
        }
    };

    const syncProductHierarchy = () => {
        ProductHierarchyService.syncSAPProductHierarchy().then(() => {
            setLoadingProductHierarchy(true);
            
            ProductHierarchyService.getProductHierarchy().then(data => {
                setProductHierarchy(data);
                setOriginalProductHierarchy(data);
                setLoadingProductHierarchy(false);
            });
        });
    };

    const headerProductHierarchyTable = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <Button type="button" icon="pi pi-fw pi-sync" title="Sync with SAP" label="Sync with SAP" size="small" outlined severity="info" onClick={syncProductHierarchy}></Button>
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText className="p-inputtext-sm" type="search" autoComplete="off" value={globalFilter} onChange={onGlobalFilterChange} placeholder="Search..." />
            </IconField>
        </div>
    );
    
    return (
        <div className="grid-nogutter">
            <div className="col-12">
                <Toast ref={toast} />
                
                <Card className="layout-main-card border-noround">
                <DataTable value={productHierarchy.filter((v) => v.level2 === '' && v.level3 === '')} 
                    dataKey="id" size="small" rowHover={true} tableStyle={{tableLayout: 'fixed'}}
                    scrollable scrollHeight="calc(100vh - 10.2rem)"
                    selectionMode="single" onSelectionChange={onProductHierarchySelectionChange}
                    editingRows={editingRows} editMode="row" onRowEditChange={onRowEditChange}
                    rowExpansionTemplate={level1ExpansionTemplate}
                    expandedRows={expandedRowsLevel1} onRowToggle={(e) => setExpandedRowsLevel1(e.data)}
                    loading={loadingProductHierarchy} header={headerProductHierarchyTable}>
                    <Column expander={allowExpansion} style={{ width: '3rem' }} />
                    <Column field="description" body={descriptionTemplate} header="Description" style={{ width: '30rem' }} />
                    <Column field="displayVds" body={displayVdsTemplate} header="Display VDS" editor={(options: ColumnEditorOptions) => displayVdsEditor(options)} style={{ width: '20rem' }} bodyStyle={{paddingLeft: "3rem"}} />
                    <Column field="onlineOnly" body={onlineOnlyTemplate} header="Online Only" editor={(options: ColumnEditorOptions) => onlineOnlyEditor(options)} style={{ width: '20rem' }} bodyStyle={{paddingLeft: "2.7rem"}} />
                    <Column field="fanwear" body={fanwearTemplate} header="Fanwear" editor={(options: ColumnEditorOptions) => fanwearEditor(options)} style={{ width: '20rem' }} bodyStyle={{paddingLeft: "2.4rem"}} />
                    <Column rowEditor body={actionTemplate} style={{ width: '8rem' }} />
                </DataTable>
                </Card>
            </div>
        </div>
    );
}

export default ProductHierarchies;